/**
 * Category chip — outlined by default, tinted when `selected`.
 *
 * Distinct from {@link StatusTag}: that one reports state, this one labels a category
 * ("Principal") or acts as a selectable filter chip (the BU picker on Assinaturas).
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX, ReactNode } from 'react';

export type TagTone = 'neutral' | 'primary' | 'water' | 'gas' | 'energy' | 'streetlight';

const TONE_STYLE: Record<TagTone, { color: string; border: string; background: string }> = {
  neutral: {
    color: 'var(--color-text-secondary)',
    border: 'var(--color-border-strong)',
    background: 'var(--color-surface)',
  },
  primary: {
    color: 'var(--color-primary)',
    border: 'var(--color-primary)',
    background: 'var(--color-primary-subtle)',
  },
  water: {
    color: 'var(--color-water)',
    border: 'var(--color-water)',
    background: 'var(--color-info-subtle)',
  },
  gas: {
    color: 'var(--color-gas)',
    border: 'var(--color-gas)',
    background: 'var(--gl-orange-10)',
  },
  energy: {
    color: 'var(--gl-yellow-dark)',
    border: 'var(--color-energy)',
    background: 'var(--color-warning-subtle)',
  },
  streetlight: {
    color: 'var(--color-streetlight)',
    border: 'var(--color-streetlight)',
    background: 'var(--gl-purple-10)',
  },
};

export interface TagProps {
  children: ReactNode;
  tone?: TagTone;
  icon?: IGLIcon['variant'];
  /** Fills the chip with the tone's tint instead of leaving it outlined. */
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  id?: string;
}

export const Tag = ({
  children,
  tone = 'neutral',
  icon,
  selected = false,
  disabled = false,
  onClick,
  id,
}: TagProps): JSX.Element => {
  const style = TONE_STYLE[tone];
  const isInteractive = onClick !== undefined && !disabled;

  const className = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 12px;
    border-radius: var(--radius-round);
    border: 1px solid ${style.border};
    background: ${selected ? style.background : 'var(--color-surface)'};
    color: ${style.color};
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    line-height: 1;
    white-space: nowrap;
    cursor: ${isInteractive ? 'pointer' : 'default'};
    opacity: ${disabled ? 0.5 : 1};
    transition: background var(--duration-fast) var(--ease-standard);
  `;

  if (onClick) {
    return (
      <button id={id} type='button' disabled={disabled} onClick={onClick} className={className}>
        {icon ? <GLIcon variant={icon} size={14} fill='currentColor' /> : null}
        {children}
      </button>
    );
  }

  return (
    <span id={id} className={className}>
      {icon ? <GLIcon variant={icon} size={14} fill='currentColor' /> : null}
      {children}
    </span>
  );
};
